/**
 * The interior registry.
 *
 * Every enterable building's interior lives on its own coordinate island and
 * is built with the SAME sequence: make a root at the island Z, parent it,
 * construct its system, register colliders under the island placement. That
 * sequence used to be copy-pasted per interior directly in PcCanvas — which is
 * why adding an interior meant editing PcCanvas in four places and why every
 * parallel change to it collided.
 *
 * Now an interior is a data row here plus its own system file. PcCanvas builds
 * them in one loop and never needs touching to add another.
 */
import * as pc from 'playcanvas'
import type { AssetZoneController } from '../assetZones'
import { setColliderPlacement } from '../colliders'
import {
  BOARD_ISLAND_Z,
  OPS_ISLAND_Z,
  LIBRARY_ISLAND_Z,
  EXCHANGE_ISLAND_Z,
  WORKSHOP_ISLAND_Z,
  CAFE_ISLAND_Z,
  DATACENTRE_ISLAND_Z,
} from './interiors'
import { BoardSystem } from './boardSystem'
import { OpsSystem } from './opsSystem'
import { LibrarySystem } from './library'
import { CampusRoomSystem } from './campusRoomSystem'
import {
  buildWorkplaceFurnishing,
  type WorkplacePlotId,
} from './workplaceFurnishing'
import type { RosterAgent } from './agentRoster'

/** What every interior system implements. */
export interface InteriorSystem {
  init(app: pc.Application, root: pc.Entity, camera: pc.Entity): void
  /** Called each frame; `active` is true only while the player is inside. */
  frame(active: boolean): void
  /** Optional spatial roster published to the one app-level talk owner. */
  getAgentTalkRoster?(): readonly RosterAgent[]
  destroy(): void
}

export interface InteriorRegistration {
  /** The plot whose door enters this interior. */
  plotId: string
  /** The interior's coordinate-island Z offset. */
  islandZ: number
  create: () => InteriorSystem
}

/**
 * The office (P3) is intentionally NOT here — it is the original interior with
 * a different construction (setWorldRoot + setupScene + the station/gallery/
 * server systems). Everything data-driven lives below.
 */
export const INTERIOR_SYSTEMS: readonly InteriorRegistration[] = [
  { plotId: 'P1', islandZ: BOARD_ISLAND_Z, create: () => new BoardSystem() },
  { plotId: 'P2', islandZ: OPS_ISLAND_Z, create: () => new OpsSystem() },
  { plotId: 'P7', islandZ: LIBRARY_ISLAND_Z, create: () => new LibrarySystem() },
  { plotId: 'P4', islandZ: EXCHANGE_ISLAND_Z, create: () => new CampusRoomSystem('exchange') },
  { plotId: 'P5', islandZ: WORKSHOP_ISLAND_Z, create: () => new CampusRoomSystem('workshop') },
  { plotId: 'P6', islandZ: CAFE_ISLAND_Z, create: () => new CampusRoomSystem('cafe') },
  { plotId: 'P8', islandZ: DATACENTRE_ISLAND_Z, create: () => new CampusRoomSystem('datacentre') },
]

/** A handle over all built interior systems, driven from the update loop. */
export interface BuiltInteriors {
  frame: (insidePlot: string | null) => void
  getAgentTalkRoster: () => readonly RosterAgent[]
  destroy: () => void
}

/**
 * Build every registered interior on its island. The collider placement is
 * scoped per interior and always cleared, so a throw in one interior's init
 * cannot leave the placement offset for the next.
 */
export function buildInteriors(
  app: pc.Application,
  camera: pc.Entity,
  assetZones: AssetZoneController<pc.Asset>,
): BuiltInteriors {
  const built = INTERIOR_SYSTEMS.map((reg) => {
    const root = new pc.Entity(`${reg.plotId}-interior`)
    root.setPosition(0, 0, reg.islandZ)
    app.root.addChild(root)
    const system = reg.create()
    setColliderPlacement({ rotY: 0, dx: 0, dz: reg.islandZ })
    let furnishing: ReturnType<typeof buildWorkplaceFurnishing>
    try {
      system.init(app, root, camera)
      furnishing = buildWorkplaceFurnishing(
        app,
        root,
        reg.plotId as WorkplacePlotId,
        assetZones,
      )
    } finally {
      setColliderPlacement(null)
    }
    // Coordinate islands isolate collision and authoring space, but they are
    // still ordinary scene nodes. The HQ camera can look far enough south to
    // include the next islands, so inactive rooms must not stay renderable.
    // Their systems and projected state remain alive; re-enabling the root is
    // enough to reveal the already-current room on entry.
    root.enabled = false
    return { reg, root, system, furnishing }
  })

  let activeTalkRoster: readonly RosterAgent[] = []
  return {
    frame(insidePlot) {
      activeTalkRoster = []
      for (const { reg, root, system, furnishing } of built) {
        const active = insidePlot === reg.plotId
        if (root.enabled !== active) root.enabled = active
        furnishing.setActive(active)
        system.frame(active)
        if (active) activeTalkRoster = system.getAgentTalkRoster?.() ?? []
      }
    },
    getAgentTalkRoster() {
      return activeTalkRoster
    },
    destroy() {
      for (const { root, system, furnishing } of built) {
        furnishing.destroy()
        system.destroy()
        root.destroy()
      }
    },
  }
}
